import { DEFAULT_LOW_BALANCE_ALERT, Plan } from '../types'
import { computeScope, includedAccounts } from './accounts'
import { addDays } from './dateRules'

export interface LowBalanceWarning {
  /** The lowest day in the window that falls below the threshold. */
  date: string
  balance: number
  threshold: number
  days: number
}

/** The amount that counts as low: the one set, or the combined balance's "tight" level. */
export function alertThreshold(plan: Plan): number {
  return plan.settings.lowBalanceAlert?.threshold ?? plan.settings.combinedRagThresholds.medium
}

/**
 * Whether the combined balance dips below the alert level in the next N days,
 * and if so its lowest point. Today counts: a balance already below the line
 * is the most urgent case, not one to leave out.
 *
 * The combined balance only — every account that counts towards the total —
 * because the setting is plan-wide, and a single account's own dips are what
 * its calendar colours are for.
 */
export function lowBalanceWarning(plan: Plan, now: Date): LowBalanceWarning | null {
  const alert = plan.settings.lowBalanceAlert ?? DEFAULT_LOW_BALANCE_ALERT
  if (!alert.enabled || includedAccounts(plan).length === 0) return null

  // Midnight: the engine's days start then, and with the time of day left on,
  // today itself would fall before the window and drop out.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const end = addDays(today, alert.days - 1)
  const threshold = alertThreshold(plan)
  let lowest: { date: string; balance: number } | null = null
  for (const day of computeScope(plan, { kind: 'all' }, today, end)) {
    if (!lowest || day.balance < lowest.balance) lowest = { date: day.date, balance: day.balance }
  }
  if (!lowest || lowest.balance >= threshold) return null
  return { ...lowest, threshold, days: alert.days }
}

/**
 * What "dismiss" remembers: this particular low point. If the plan changes
 * and the low moves to another day or amount, the warning comes back — it's
 * new information, not the thing that was waved away.
 */
export function warningKey(w: LowBalanceWarning): string {
  return `${w.date}|${w.balance.toFixed(2)}|${w.threshold.toFixed(2)}`
}
